'use strict'

/**
 * Schedule-related utility functions shared across Territory Workbench tabs.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const WORK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const FREQUENCY_MAP = {
  D: 'Daily',
  MWF: 'Every Other Day (M-W-F)',
  TW: 'Twice a Week',
  W: 'Weekly',
  BW: 'Bi-Weekly',
  BW_A: 'Bi-Weekly (Week A)',
  BW_B: 'Bi-Weekly (Week B)',
  M: 'Monthly',
  Q: 'Quarterly',
  SA: 'Semi-Annually',
  A: 'Annually',
  UNKNOWN: 'Unknown'
}

const LONG_CYCLE_CODES = new Set(['M', 'M1', 'M2', 'M3', 'M4', 'Q', 'SA', 'A'])

// ── Date helpers ──────────────────────────────────────────────

export const parseYmd = (value) => {
  const input = String(value || '').trim()
  const date = input ? new Date(`${input}T00:00:00`) : new Date()
  if (Number.isNaN(date.getTime())) return new Date()
  return date
}

export const toYmd = (date) => {
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

export const addDays = (date, days) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export const getMonday = (date) => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  return addDays(d, diff)
}

export const getIsoWeek = (dateInput) => {
  const date = new Date(Date.UTC(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
}

export const getWeekTypeForDate = (date) => {
  const week = getIsoWeek(date)
  return week % 2 === 0 ? 'A' : 'B'
}

export const getDayName = (date) => DAY_NAMES[new Date(date).getDay()] || null

// ── Day / frequency helpers ───────────────────────────────────

export const normalizeDaysForRow = (row = {}) => {
  const days = Array.isArray(row.daysOfWeek) ? row.daysOfWeek.filter(Boolean) : []
  if (days.length) return days
  if (Array.isArray(row.preferredDays) && row.preferredDays.length) {
    return row.preferredDays.filter(Boolean)
  }
  if (row.preferredDay) return [row.preferredDay]
  return []
}

export const isBiWeeklyCode = (frequencyCode, frequencyLabel) => {
  const code = String(frequencyCode || '').trim().toUpperCase()
  if (code === 'BW' || code === 'BW_A' || code === 'BW_B') return true
  return String(frequencyLabel || '').trim().toLowerCase() === 'bi-weekly'
}

export const rowWeekType = (row = {}) => {
  const explicit = String(row.weekType || '').trim().toUpperCase()
  if (explicit === 'A' || explicit === 'B') return explicit
  const code = String(row.frequencyCode || '').trim().toUpperCase()
  if (code === 'BW_A') return 'A'
  if (code === 'BW_B') return 'B'
  return null
}

export const isLongCycleCode = (frequencyCode, frequencyLabel) => {
  const code = String(frequencyCode || '').trim().toUpperCase()
  if (LONG_CYCLE_CODES.has(code)) return true
  const label = String(frequencyLabel || '').trim().toLowerCase()
  return ['monthly', 'quarterly', 'semi-annually', 'annually'].includes(label)
}

// ── Scheduling logic ──────────────────────────────────────────

export const isDueOnDate = (row = {}, dateInput) => {
  const date = new Date(dateInput)
  const dayName = getDayName(date)
  const days = normalizeDaysForRow(row)
  const hasDayRule = days.length > 0
  if (hasDayRule && dayName && !days.includes(dayName)) return false

  if (isBiWeeklyCode(row.frequencyCode, row.frequency)) {
    const required = rowWeekType(row)
    if (!required) return false
    return getWeekTypeForDate(date) === required
  }

  // Long-cycle properties (monthly, quarterly, semi-annual, annual):
  // day-of-week alone is not enough — require an actual scheduledVisitDates match
  if (isLongCycleCode(row.frequencyCode, row.frequency)) {
    const dateStr = toYmd(date)
    const visits = Array.isArray(row.scheduledVisitDates) ? row.scheduledVisitDates : []
    if (!visits.length) return false
    return visits.some(v => {
      const vDate = String(v.date || v || '').slice(0, 10)
      return vDate === dateStr
    })
  }

  return true
}

export const countDueOccurrences = (row = {}, dates = []) => {
  if (!Array.isArray(dates) || dates.length === 0) return 0
  return dates.reduce((sum, date) => sum + (isDueOnDate(row, date) ? 1 : 0), 0)
}

// ── Frequency label ───────────────────────────────────────────

/**
 * Returns a human-readable frequency label from a frequency code string.
 * Works for both the MapTab pattern (code-only) and the AnchorsTab pattern
 * (item with frequencyLabel + frequencyCode).
 *
 * Called as getFrequencyLabel(code) or getFrequencyLabel(item).
 */
export const getFrequencyLabel = (codeOrItem) => {
  if (codeOrItem && typeof codeOrItem === 'object') {
    // AnchorsTab / PropertiesTab item pattern
    if (codeOrItem.frequencyLabel) return codeOrItem.frequencyLabel
    const code = String(codeOrItem.frequencyCode || '').trim().toUpperCase()
    return FREQUENCY_MAP[code] || 'Unknown'
  }
  // MapTab code-only pattern
  const code = String(codeOrItem || '').trim().toUpperCase()
  return FREQUENCY_MAP[code] || 'Unknown'
}

// Alias for backward compatibility with MapTab's name
export const getFrequencyLabelFromCode = (frequencyCode) => {
  const code = String(frequencyCode || '').trim().toUpperCase()
  return FREQUENCY_MAP[code] || 'Unknown'
}

// ── Day sorting (from PropertiesTab) ──────────────────────────

export const sortDays = (days = []) => {
  return [...new Set(days.filter(Boolean))]
    .sort((a, b) => WORK_DAYS.indexOf(a) - WORK_DAYS.indexOf(b))
}

// ── Composite label (from PropertiesTab) ──────────────────────

export const buildFrequencyLabel = (row) => {
  const parts = [row.frequency || 'Unspecified']
  if (row.preferredDay && (row.frequency === 'Weekly' || row.frequency === 'Bi-Weekly')) {
    parts.push(row.preferredDay.slice(0, 3))
  }
  if (Array.isArray(row.preferredDays) && row.preferredDays.length > 0 && row.frequency === 'Twice a Week') {
    parts.push(sortDays(row.preferredDays).map(d => d.slice(0, 3)).join(', '))
  }
  if (row.weekType) {
    parts.push(`(Wk ${row.weekType})`)
  }
  return parts.join(' ')
}

// ── Re-export constants for consumers ─────────────────────────

export { DAY_NAMES, WORK_DAYS }
